import { ArrowRight, Shield, CheckCircle, TrendingUp, Award, Users, Star, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import SEO from './SEO';
import { pageSEO, faqSchemas } from '../seoData';
import RelatedServices from './RelatedServices';

const RetirementPlanningIndia = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.retirementPlanningIndia} faqJsonLd={faqSchemas.retirementPlanningIndia} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                  COMPREHENSIVE RETIREMENT SOLUTIONS
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Retirement Planning India
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Secure Your Golden Years
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Expert retirement planning services to help you retire comfortably and maintain your lifestyle. 
                Build a sufficient corpus through systematic investing, tax-efficient strategies, and professional 
                wealth management by SEBI-registered advisors.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl"
                >
                  Plan My Retirement
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/calculator"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
                >
                  Retirement Calculator
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?w=800&q=85" 
                  alt="Retirement planning services India - Professional retirement corpus calculation and investment advisory for secure financial future and post-retirement income"
                  className="w-full h-[500px]"
                  priority={true}
                  objectFit="cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                25+ Years
              </div>
              <div className="text-gray-600">Retirement Advisory</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                500+
              </div>
              <div className="text-gray-600">Successful Retirements</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                ₹100 Cr+
              </div>
              <div className="text-gray-600">Retirement Corpus Managed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                SEBI
              </div>
              <div className="text-gray-600">Registered Advisors</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Retirement Planning */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Retirement Planning is Critical in India?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Challenges that make professional retirement planning essential
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Increasing Life Expectancy</h3>
              <p className="text-gray-600 leading-relaxed">
                Indians now live 70+ years on average. With 25-30 years of retirement life, you need a 
                substantial corpus to maintain your lifestyle without regular income.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Rising Healthcare Costs</h3>
              <p className="text-gray-600 leading-relaxed">
                Medical expenses increase 10-12% annually in India. Without proper planning, healthcare costs 
                can deplete your retirement savings rapidly.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Inflation Impact</h3>
              <p className="text-gray-600 leading-relaxed">
                6-7% annual inflation means your expenses will triple in 20 years. Your retirement corpus 
                must beat inflation to preserve purchasing power.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Retirement Solutions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Retirement Planning Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions for a worry-free retirement
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Retirement Corpus Calculation",
                description: "Calculate how much you need to retire comfortably based on your lifestyle, inflation, and life expectancy",
                bestFor: "All ages"
              },
              {
                title: "Goal-Based SIP Planning",
                description: "Systematic investment plans designed to accumulate sufficient retirement corpus through disciplined investing",
                bestFor: "Working professionals"
              },
              {
                title: "NPS (National Pension System)",
                description: "Tax-efficient NPS investments with Tier I and Tier II accounts for retirement wealth creation",
                bestFor: "Salaried & self-employed"
              },
              {
                title: "EPF & VPF Optimization",
                description: "Maximize EPF benefits and utilize Voluntary Provident Fund for tax-free retirement savings",
                bestFor: "Salaried employees"
              },
              {
                title: "Retirement Portfolio Management",
                description: "Professional management of retirement corpus with optimal asset allocation and regular rebalancing",
                bestFor: "Pre-retirees & retirees"
              },
              {
                title: "Post-Retirement Income Planning",
                description: "Generate regular monthly income from your retirement corpus through SWP, dividends, and annuities",
                bestFor: "Retirees"
              }
            ].map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{service.description}</p>
                <div className="text-sm font-semibold text-blue-600">Best For: {service.bestFor}</div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-xl"
            >
              View All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Retirement Planning Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Retirement Planning Process
            </h2>
            <p className="text-xl text-gray-600">
              Systematic approach to secure retirement
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Lifestyle Analysis",
                description: "Understand your current lifestyle, retirement goals, and expected expenses post-retirement"
              },
              {
                step: "02",
                title: "Corpus Calculation",
                description: "Calculate required retirement corpus considering inflation, life expectancy, and lifestyle"
              },
              {
                step: "03",
                title: "Investment Strategy",
                description: "Design systematic investment plan with optimal asset allocation to build the corpus"
              },
              {
                step: "04",
                title: "Review & Adjust",
                description: "Regular portfolio reviews and rebalancing as you approach retirement"
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Retirement Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Real people who achieved their retirement dreams
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Ramesh & Sunita Kumar",
                location: "Mumbai",
                testimonial: "Started retirement planning at 35. Their systematic approach helped us accumulate ₹3.5 crores by 55. Now enjoying stress-free retirement with ₹2 lakh monthly income. Best decision ever!"
              },
              {
                name: "Dr. Anand Rao",
                location: "Bangalore",
                testimonial: "Retired at 58 with ₹2.8 crore corpus thanks to their planning. They manage my post-retirement portfolio beautifully. Getting stable ₹1.5L monthly income without touching principal!"
              },
              {
                name: "Meera Patel",
                location: "Ahmedabad",
                testimonial: "Single woman planning early retirement. They helped me build ₹1.2 crore corpus through SIPs and smart asset allocation. Retired at 52 and living my dream life!"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.testimonial}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.name.split(' ')[0][0]}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Retirement Planning FAQs
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about retirement planning in India
            </p>
          </div>
          
          <div className="space-y-6">
            {[
              {
                question: "How much corpus do I need to retire in India?",
                answer: "It depends on your lifestyle. As a rule of thumb, you need 25-30 times your annual expenses. For example, if you need ₹6 lakhs/year (₹50k/month), you need ₹1.5-1.8 crores. We calculate this precisely based on your goals, inflation, and life expectancy."
              },
              {
                question: "When should I start retirement planning?",
                answer: "The earlier, the better! Starting in your 30s gives you 25-30 years to build wealth through compounding. Even if you're in your 40s or 50s, it's not too late - we'll create an accelerated plan to meet your retirement goals."
              },
              {
                question: "What are the best investment options for retirement in India?",
                answer: "Diversified portfolio including equity mutual funds (for growth), NPS (tax benefits), EPF/PPF (stability), and debt funds (income generation). We create optimal asset allocation based on your age and retirement timeline."
              },
              {
                question: "How can I generate monthly income after retirement?",
                answer: "Multiple options: Systematic Withdrawal Plan (SWP) from mutual funds, pension from NPS, interest from fixed deposits/bonds, dividend income, and rental income. We design post-retirement income strategies for stable monthly cash flow."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['mutualFund', 'fd', 'bonds', 'pms', 'nri', 'calculator']} />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Start Planning Your Dream Retirement Today!
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Don't leave your golden years to chance. Get expert retirement planning guidance 
            and secure your financial future. Schedule your free consultation now!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-blue-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-2xl"
            >
              Plan My Retirement
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
            <Link
              to="/calculator"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-5 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              Calculate My Corpus
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RetirementPlanningIndia;